import { motion } from "framer-motion";
import {
  ArrowRight,
  CalendarClock,
  CarFront,
  CheckCircle2,
  Clock3,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const HERO_BG = "/experience-media/photos/blackride-jeddah-arrival-makkah-transfer.jpg";
const WHATSAPP_LINK =
  "https://wa.me/966540423401?text=Hello%20BlackRide%0A%0AI%20would%20like%20to%20book%20a%20private%20Jeddah%20Airport%20to%20Makkah%20transfer.%0A%0AFlight%20details%3A%0APickup%20time%3A%0APassengers%3A%0ALuggage%3A%0APreferred%20vehicle%20class%3A";
const CALL_LINK = "tel:+966540423401";

const COPY = {
  en: {
    eyebrow: "Private Jeddah Airport to Makkah Transfers",
    headline: "A calmer arrival from Jeddah Airport to Makkah, designed for premium travelers.",
    subheadline:
      "BlackRide helps families, Umrah guests, executives, and hosted travelers move from King Abdulaziz International Airport to Makkah with a more polished private chauffeur experience, 24/7 coordination, and meet-and-greet included.",
    primaryCta: "Book Airport Transfer on WhatsApp",
    secondaryCta: "Call BlackRide",
    tertiaryCta: "View Route Details",
    routeLabel: "Most booked route",
    routeTitle: "Jeddah Airport → Makkah",
    routeSubtitle: "Private chauffeur arrival with meet-and-greet, luggage support, and premium vehicle guidance.",
    routePoints: [
      "24/7 airport coverage",
      "TGA-approved service",
      "Owned premium fleet",
      "Family, executive, and Umrah-ready",
    ],
    miniStats: [
      { icon: Clock3, label: "Always on", value: "24/7 booking desk" },
      { icon: ShieldCheck, label: "Service standard", value: "TGA-approved" },
      { icon: CarFront, label: "Vehicle planning", value: "Sedan, SUV, van" },
    ],
    trustTitle: "What premium guests expect on arrival",
    trustPoints: [
      {
        icon: MapPin,
        title: "Direct airport-to-hotel clarity",
        body: "One route, one team, one cleaner handoff after landing.",
      },
      {
        icon: Users,
        title: "Better fit for families and elders",
        body: "Vehicle guidance built around luggage, boarding comfort, and group size.",
      },
      {
        icon: CalendarClock,
        title: "Structured around real flight timing",
        body: "Ideal for international arrivals, concierge bookings, and premium itineraries.",
      },
    ],
    reassurance:
      "Preferred by international travelers, GCC families, hotel guests, and executive hosts who want a more dependable Saudi arrival experience.",
  },
  ar: {
    eyebrow: "نقل خاص من مطار جدة إلى مكة",
    headline: "وصول أكثر هدوءاً من مطار جدة إلى مكة، مصمم للمسافرين الباحثين عن مستوى راقٍ.",
    subheadline:
      "تساعد بلاك رايد العائلات، وضيوف العمرة، والتنفيذيين، والضيوف القادمين إلى المملكة على الانتقال من مطار الملك عبدالعزيز الدولي إلى مكة عبر تجربة سائق خاص أكثر أناقة مع تنسيق 24/7 وخدمة استقبال متضمنة.",
    primaryCta: "احجز نقل المطار عبر واتساب",
    secondaryCta: "اتصل ببلاك رايد",
    tertiaryCta: "اعرض تفاصيل المسار",
    routeLabel: "المسار الأكثر طلباً",
    routeTitle: "مطار جدة ← مكة",
    routeSubtitle: "وصول بسائق خاص مع استقبال، ومساعدة بالأمتعة، وترشيح لفئة المركبة المناسبة.",
    routePoints: [
      "خدمة مطار 24/7",
      "خدمة معتمدة من هيئة النقل",
      "أسطول مملوك ومتميز",
      "مناسب للعائلات والتنفيذيين وضيوف العمرة",
    ],
    miniStats: [
      { icon: Clock3, label: "جاهزية دائمة", value: "مكتب حجز 24/7" },
      { icon: ShieldCheck, label: "معيار الخدمة", value: "معتمد من هيئة النقل" },
      { icon: CarFront, label: "تخطيط المركبة", value: "سيدان، SUV، فان" },
    ],
    trustTitle: "ما الذي يتوقعه الضيف الراقي عند الوصول",
    trustPoints: [
      {
        icon: MapPin,
        title: "وضوح مباشر من المطار إلى الفندق",
        body: "مسار واحد وفريق واحد وانتقال أكثر سلاسة بعد الهبوط.",
      },
      {
        icon: Users,
        title: "أنسب للعائلات وكبار السن",
        body: "ترشيح المركبة حسب الأمتعة، وراحة الصعود، وحجم المجموعة.",
      },
      {
        icon: CalendarClock,
        title: "مبني على توقيت الرحلة الحقيقي",
        body: "مثالي للوصولات الدولية، وحجوزات الكونسيرج، والبرامج الراقية.",
      },
    ],
    reassurance:
      "مفضلة لدى المسافرين الدوليين، وعائلات الخليج، وضيوف الفنادق، والجهات المستضيفة الباحثة عن وصول أكثر موثوقية داخل السعودية.",
  },
};

export default function Hero() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-[#050505]"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="BlackRide private chauffeur arrival from Jeddah Airport to Makkah"
          className="h-full w-full object-cover object-[78%_center] lg:object-[72%_center]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(3,3,3,0.985)_0%,rgba(3,3,3,0.92)_32%,rgba(3,3,3,0.72)_54%,rgba(3,3,3,0.48)_74%,rgba(3,3,3,0.7)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(199,162,57,0.1),transparent_34%),linear-gradient(180deg,rgba(5,5,5,0.18)_0%,rgba(5,5,5,0.28)_38%,rgba(5,5,5,0.82)_78%,rgba(5,5,5,0.96)_100%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-14 pt-24 md:px-12 md:pb-20 md:pt-32 lg:pb-24">
        <div className="grid items-start gap-8 lg:grid-cols-[1.06fr_0.94fr] lg:gap-12">
          <div className="max-w-3xl pt-3 md:pt-4">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#C7A239]/20 bg-[#120f09]/85 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.26em] text-[#D7B458]"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {copy.eyebrow}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.08 }}
              className="mb-5 max-w-3xl font-['Playfair_Display'] text-[2.65rem] font-semibold leading-[0.98] tracking-tight text-white sm:text-5xl lg:text-[4.15rem]"
              data-testid="hero-headline"
            >
              {copy.headline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.16 }}
              className="mb-7 max-w-xl text-[15px] leading-7 text-[#ddd5c8] md:max-w-2xl md:text-lg md:leading-8"
              data-testid="hero-subheadline"
            >
              {copy.subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.24 }}
              className="mb-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
            >
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-6 py-4 font-semibold text-black shadow-[0_18px_38px_rgba(199,162,57,0.24)] transition-all duration-300 hover:bg-[#D4B34A] hover:shadow-[0_24px_48px_rgba(199,162,57,0.32)] md:px-7"
                data-testid="hero-whatsapp-cta"
                data-track="hero_whatsapp_cta"
              >
                <MessageCircle className="h-5 w-5" />
                {copy.primaryCta}
              </a>

              <a
                href="/jeddah-airport-to-makkah-transfer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-6 py-4 font-medium text-white transition-all duration-300 hover:border-white/30 hover:bg-white/[0.07] md:px-7"
              >
                {copy.tertiaryCta}
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href={CALL_LINK}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-4 font-medium text-[#d8d2c8] transition-all duration-300 hover:border-white/25 hover:text-white md:px-7"
                data-testid="hero-call-cta"
                data-track="hero_call_cta"
              >
                <Phone className="h-5 w-5" />
                {copy.secondaryCta}
              </a>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.3 }}
              className="mb-6 max-w-xl text-sm leading-7 text-[#a79f93] md:max-w-2xl"
            >
              {copy.reassurance}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.36 }}
              className="grid gap-3 sm:grid-cols-3"
            >
              {copy.miniStats.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-white/10 bg-black/34 p-4 backdrop-blur-md"
                  >
                    <Icon className="mb-3 h-4 w-4 text-[#C7A239]" />
                    <p className="text-xs uppercase tracking-[0.22em] text-[#8f8677]">{item.label}</p>
                    <p className="mt-2 text-sm font-medium text-white">{item.value}</p>
                  </div>
                );
              })}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.42 }}
              className="mt-6 lg:hidden"
            >
              <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(15,15,15,0.95),rgba(8,8,8,0.98))] shadow-[0_20px_60px_rgba(0,0,0,0.32)] backdrop-blur-xl">
                <div className="border-b border-white/10 px-5 py-4">
                  <p className="mb-2 text-[11px] uppercase tracking-[0.24em] text-[#C7A239]">{copy.routeLabel}</p>
                  <h2 className="font-['Playfair_Display'] text-[1.8rem] leading-tight text-white">
                    {copy.routeTitle}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-[#c8c1b5]">
                    {copy.routeSubtitle}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 px-5 py-4">
                  {copy.routePoints.map((point) => (
                    <div
                      key={point}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-2"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-[#C7A239]" />
                      <span className="text-[13px] leading-5 text-white/90">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="hidden lg:block lg:pt-6"
          >
            <div className="overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,17,17,0.94),rgba(9,9,9,0.96))] shadow-[0_28px_90px_rgba(0,0,0,0.4)] backdrop-blur-xl">
              <div className="border-b border-white/10 px-6 py-5 md:px-7">
                <p className="mb-3 text-[11px] uppercase tracking-[0.28em] text-[#C7A239]">{copy.routeLabel}</p>
                <h2 className="font-['Playfair_Display'] text-3xl leading-tight text-white md:text-[2.4rem]">
                  {copy.routeTitle}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-7 text-[#c8c1b5]">
                  {copy.routeSubtitle}
                </p>
              </div>

              <div className="grid gap-6 px-6 py-6 md:px-7">
                <div className="grid gap-3 sm:grid-cols-2">
                  {copy.routePoints.map((point) => (
                    <div key={point} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-4">
                      <CheckCircle2 className="mt-0.5 h-4.5 w-4.5 shrink-0 text-[#C7A239]" />
                      <span className="text-sm leading-6 text-white/90">{point}</span>
                    </div>
                  ))}
                </div>

                <div className="rounded-[28px] border border-[#C7A239]/20 bg-[radial-gradient(circle_at_top,rgba(199,162,57,0.12),transparent_55%),#0b0b0b] p-5">
                  <p className="mb-4 text-sm uppercase tracking-[0.22em] text-[#C7A239]">{copy.trustTitle}</p>
                  <div className="space-y-4">
                    {copy.trustPoints.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div key={item.title} className="flex gap-4">
                          <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#C7A239]/10">
                            <Icon className="h-4.5 w-4.5 text-[#C7A239]" />
                          </div>
                          <div>
                            <h3 className="text-base font-medium text-white">{item.title}</h3>
                            <p className="mt-1 text-sm leading-7 text-[#a8a093]">{item.body}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
